/**
 * RecentSearch API Service
 * Patterned after icors.service.ts
 *
 * Purpose: Fetch and manage recent search data for a user
 *
 * Dependencies:
 * - @repo/shared-utils/cache - Function memoization
 * - @repo/shared-utils/session - User session management
 * - connection-manager (GlobalApisService) - External npm package
 */

import { createCacheAsync } from "@repo/shared-utils/cache";
import { getSessionData } from "@repo/shared-utils/session";
import { request } from "../core/request";
import { API_RESPONSE_CODES, OPERATION_IDS, REST_CONNECTION_FROM } from "./constants";

interface IRecentSearch {
  query?: string;
  timestamp?: string;
  type?: string;
}

interface IRecentSearchResponse {
  statusCode: number | string;
  description: string;
  data: {
    searches: IRecentSearch[];
  };
}

interface IRecentSearchRequest {
  mobileNo: string;
  consumer_business_name: string;
}

const buildRecentSearchErrorResponse = (
  description = "Error fetching recent searches"
): IRecentSearchResponse => ({
  statusCode: API_RESPONSE_CODES.STATUS_CODE_7002,
  description,
  data: { searches: [] },
});

/**
 * Get recent searches for the current user
 *
 * Features:
 * - Automatically fetches user identifier from session
 * - Caches results per user to avoid duplicate API calls
 * - Returns structured recent search list with status codes
 *
 * @returns Promise<IRecentSearchResponse> - Recent searches with status
 */
export const getRecentSearches = async (business_key: string): Promise<IRecentSearchResponse> => {
  try {
    const { mobileNo } = await getSessionData();
    if (!mobileNo) {
      return buildRecentSearchErrorResponse("Mobile number not found in session");
    }

    const response = (await createCacheAsync(
      `${OPERATION_IDS.recentSearches}-${mobileNo}`,
      async () =>
        await request<IRecentSearchResponse, IRecentSearchRequest>({
          operationId: OPERATION_IDS.recentSearches,
          requestData: {
            mobileNo,
            consumer_business_name: business_key,
          },
          additionalHeaders: {
            apiVersion: "v1",
          },
          connectionFrom: REST_CONNECTION_FROM,
        })
    )) as IRecentSearchResponse;
    return response;
  } catch (error) {
    console.error(error);
    return buildRecentSearchErrorResponse();
  }
};

/**
 * Get search query from recent search data
 * @param searchData - Recent search data
 * @returns Search query or empty string
 */
export const getSearchQuery = (searchData?: IRecentSearch): string =>
  searchData?.query ? searchData.query : "";

/**
 * Get search timestamp from recent search data
 * @param searchData - Recent search data
 * @returns Timestamp or empty string
 */
export const getSearchTimestamp = (searchData?: IRecentSearch): string =>
  searchData?.timestamp ? searchData.timestamp : "";

/**
 * Get recent searches filtered by type
 * @param type - Search type (e.g., product, category)
 * @returns Array of filtered searches
 */
export const recentSearchAPI = async (type: string): Promise<IRecentSearch[]> => {
  const searchType = type?.length ? type : "";
  const response = await getRecentSearches("b2b_landing_page");
  const searches =
    response?.data?.searches?.filter((search: IRecentSearch) => search.type === searchType) || [];
  return searches;
};
